'use client'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { colorType } from '@/lib/color/type'
import { serviceType } from '@/lib/service/server'
import { tagType } from '@/lib/tag'
import { ChevronDown } from 'lucide-react'
import { useSearchParams } from 'next/navigation'
import { useState } from 'react'

export const SortPopover = ({
  service,
  priceRange,
  colors,
  tags,
}: {
  service: serviceType
  priceRange: boolean
  colors: colorType[]
  tags: tagType[]
}) => {
  const searchParams = useSearchParams()
  const [price, setPrice] = useState({
    min: searchParams.get('price_from') || 0,
    max: searchParams.get('price_to') || 0,
  })
  const [colorIds, setColorIds] = useState<number[]>(
    searchParams
      .get('color_ids')
      ?.split(',')
      .map((id) => parseInt(id)) || [],
  )
  const [tagIds, setTagIds] = useState<number[]>(
    searchParams
      .get('tag_id')
      ?.split(',')
      .map((id) => parseInt(id)) || [],
  )
  return (
    <Popover>
      <PopoverTrigger className="sort-button button-color main-color text-white py-2 px-4 rounded-lg flex items-center">
        <span className="text-sm">条件を設定する</span>
        <ChevronDown width={15} height={15} className="ml-2" />
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        avoidCollisions={false}
        className="bg-white w-[330px] mr-2 z-30"
        onOpenAutoFocus={(e) => e.preventDefault()}
        style={{ marginRight: '160px' }}
      >
        <div className="text-sm my-2">
          {priceRange && (
            <div className="">
              <>
                <span className="font-semibold">価格</span>
                <div className="flex items-center mt-2 mb-4">
                  <input
                    id="name"
                    type="number"
                    placeholder="最低価格"
                    className="w-1/2 border border-gray-300 rounded-md p-1"
                    value={price.min}
                    onChange={(e) => {
                      setPrice({ ...price, min: parseInt(e.target.value) })
                    }}
                  />
                  <span className="ml-1">円</span>
                  <span className="mx-2">〜</span>
                  <input
                    type="number"
                    placeholder="最高価格"
                    className="w-1/2 border border-gray-300 rounded-md p-1"
                    value={price.max}
                    onChange={(e) => {
                      setPrice({ ...price, max: parseInt(e.target.value) })
                    }}
                  />
                  <span className="ml-1">円</span>
                </div>
              </>
            </div>
          )}
          {tags.length > 0 && (
            <div className="">
              <span className="font-semibold">キーワード</span>
              <div className="flex items-center mt-2 mb-4 flex-wrap">
                {tags.map((tag) => (
                  <div
                    key={tag.tag_id}
                    className="rounded-md p-2 mr-2 mb-2 min-w-[80px] text-center cursor-pointer"
                    style={{
                      backgroundColor: tagIds.includes(tag.tag_id)
                        ? service.main_color
                        : 'white',
                      color: tagIds.includes(tag.tag_id)
                        ? 'white'
                        : service.main_color,
                      border: tagIds.includes(tag.tag_id)
                        ? 'none'
                        : `1px solid ${service.main_color}`,
                    }}
                    onClick={() => {
                      if (tagIds.includes(tag.tag_id)) {
                        setTagIds(tagIds.filter((id) => id !== tag.tag_id))
                      } else {
                        setTagIds([...tagIds, tag.tag_id])
                      }
                    }}
                  >
                    {tag.name.trim()}
                  </div>
                ))}
              </div>
            </div>
          )}
          {colors.length > 0 && (
            <div className="">
              <span className="font-semibold">カラー</span>
              <div className="flex items-center mt-2 mb-4 flex-wrap">
                {colors.map((color) => (
                  <div className="w-1/2 mb-1" key={color.color_id}>
                    <label>
                      <input
                        type="checkbox"
                        className="mr-1"
                        checked={colorIds.includes(color.color_id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setColorIds([...colorIds, color.color_id])
                          } else {
                            setColorIds(
                              colorIds.filter((id) => id !== color.color_id),
                            )
                          }
                        }}
                      />
                      {color.name.trim()}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="mt-4 text-center">
            <button
              className="button-color main-color text-white py-2 px-8 rounded-lg"
              onClick={() => {
                if (typeof price.min === 'string' && price.min === '') {
                  price.min = '0'
                }
                if (typeof price.max === 'string' && price.max === '') {
                  price.max = '0'
                }
                if (
                  parseInt(price.min as string) > parseInt(price.max as string)
                ) {
                  alert('最低価格は最高価格よりも低くしてください')
                  return
                }
                const currentUrl = new URL(window.location.href)
                if (parseInt(price.max as string) > 0) {
                  currentUrl.searchParams.set(
                    'price_from',
                    price.min.toString(),
                  )
                  currentUrl.searchParams.set('price_to', price.max.toString())
                }
                if (tagIds.length > 0) {
                  currentUrl.searchParams.set('tag_id', tagIds.join(','))
                } else if (currentUrl.searchParams.has('tag_id')) {
                  currentUrl.searchParams.delete('tag_id')
                }
                if (colorIds.length) {
                  currentUrl.searchParams.set('color_ids', colorIds.join(','))
                } else if (currentUrl.searchParams.has('color_ids')) {
                  currentUrl.searchParams.delete('color_ids')
                }
                window.location.href = currentUrl.href
              }}
            >
              検索する
            </button>
            <button
              className="text-gray-500 py-2 px-8 rounded-lg ml-2"
              style={{
                border: `1px solid ${service.main_color}`,
                color: `${service.main_color}`,
              }}
              onClick={() => {
                setPrice({ min: 0, max: 0 })
                setColorIds([])
                setTagIds([])
              }}
            >
              リセット
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
